import type { WizardStepProps } from '../../../../types/builder.types'
import { getAggregatableFields, getAllFields } from '../../../../config/builder-schemas'
import { useDataSources } from '../../../../hooks/useDataSources'
import { useMemo } from 'react'
import {
  fieldsFromCustomQuery,
  getCustomQueryConfigSignature,
} from '../../../../utils/custom-query-builder-fields'

export function ScatterPlotConfiguration({ config, onChange, onNext, onPrev }: WizardStepProps) {
  const { dataSources } = useDataSources()

  const cqSig = useMemo(
    () => getCustomQueryConfigSignature(config.customQuery),
    [config.customQuery]
  )

  // Get fields based on data source
  const { aggregatableFields, allFields } = useMemo(() => {
    if (!config.dataSource) return { aggregatableFields: [], allFields: [] }

    if (
      (config.dataSource === 'custom_query' ||
        (config.dataSource?.includes('ckan_data_generic') && config.customQuery)) &&
      config.customQuery?.config?.selectedFields?.length
    ) {
      const fieldsArray = fieldsFromCustomQuery(config.customQuery)
      const numeric = fieldsArray.filter((f) => f.type === 'number' || f.aggregatable)
      return {
        aggregatableFields: numeric.length ? numeric : fieldsArray,
        allFields: fieldsArray,
      }
    }

    const dynamicSource = dataSources.find(ds => ds.tableName === config.dataSource)
    if (dynamicSource) {
      return {
        aggregatableFields: dynamicSource.fields.filter(f => f.aggregatable),
        allFields: dynamicSource.fields
      }
    }

    return {
      aggregatableFields: getAggregatableFields(config.dataSource),
      allFields: getAllFields(config.dataSource)
    }
  }, [dataSources, config.dataSource, cqSig])

  const scatterConfig = config.scatterPlotConfig || { xAxisKey: '', yAxisKey: '' }
  const isValid = Boolean(
    config.metadata?.title &&
    scatterConfig.xAxisKey &&
    scatterConfig.yAxisKey
  )

  return (
    <div className="max-w-3xl mx-auto">
      <button onClick={onPrev} className="mb-6 text-green-600 hover:underline">
        ← Volver
      </button>

      <h2 className="text-2xl font-bold mb-2">Configurar Gráfico de Dispersión</h2>
      <p className="text-gray-600 mb-6">
        Analiza la correlación entre dos o más variables numéricas
      </p>

      <div className="space-y-6 bg-white border rounded-lg p-6">
        {/* Título */}
        <div>
          <label className="block font-medium mb-2">
            Título <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            placeholder="Ej: Correlación Superficie-Carbono"
            className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            value={config.metadata?.title || ''}
            onChange={(e) =>
              onChange({
                ...config,
                metadata: { ...config.metadata, title: e.target.value },
              })
            }
          />
        </div>

        {/* Eje X */}
        <div>
          <label className="block font-medium mb-2">
            Variable Eje X <span className="text-red-500">*</span>
          </label>
          <select
            className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            value={scatterConfig.xAxisKey || ''}
            onChange={(e) =>
              onChange({
                ...config,
                scatterPlotConfig: { ...scatterConfig, xAxisKey: e.target.value },
              })
            }
          >
            <option value="">Selecciona un campo...</option>
            {aggregatableFields.map((field) => (
              <option key={field.name} value={field.name}>
                {field.label}
              </option>
            ))}
          </select>
          <p className="text-sm text-gray-500 mt-1">
            Variable numérica para el eje horizontal
          </p>
        </div>

        {/* Eje Y */}
        <div>
          <label className="block font-medium mb-2">
            Variable Eje Y <span className="text-red-500">*</span>
          </label>
          <select
            className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            value={scatterConfig.yAxisKey || ''}
            onChange={(e) =>
              onChange({
                ...config,
                scatterPlotConfig: { ...scatterConfig, yAxisKey: e.target.value },
              })
            }
          >
            <option value="">Selecciona un campo...</option>
            {aggregatableFields.map((field) => (
              <option key={field.name} value={field.name}>
                {field.label}
              </option>
            ))}
          </select>
          <p className="text-sm text-gray-500 mt-1">
            Variable numérica para el eje vertical
          </p>
        </div>

        {/* Eje Z (Tamaño de burbujas) - Opcional */}
        <div>
          <label className="block font-medium mb-2">
            Variable Eje Z - Tamaño de Burbujas (opcional)
          </label>
          <select
            className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            value={scatterConfig.zAxisKey || ''}
            onChange={(e) =>
              onChange({
                ...config,
                scatterPlotConfig: {
                  ...scatterConfig,
                  zAxisKey: e.target.value || undefined,
                },
              })
            }
          >
            <option value="">Ninguno (tamaño uniforme)</option>
            {aggregatableFields.map((field) => (
              <option key={field.name} value={field.name}>
                {field.label}
              </option>
            ))}
          </select>
          <p className="text-sm text-gray-500 mt-1">
            El tamaño de cada punto será proporcional a este valor
          </p>
        </div>

        {/* Campo de agrupación - Opcional */}
        <div>
          <label className="block font-medium mb-2">
            Agrupar por (opcional)
          </label>
          <select
            className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            value={scatterConfig.groupKey || ''}
            onChange={(e) =>
              onChange({
                ...config,
                scatterPlotConfig: {
                  ...scatterConfig,
                  groupKey: e.target.value || undefined,
                },
              })
            }
          >
            <option value="">Sin agrupar</option>
            {allFields.map((field) => (
              <option key={field.name} value={field.name}>
                {field.label}
              </option>
            ))}
          </select>
          <p className="text-sm text-gray-500 mt-1">
            Colorea los puntos según categorías (ej: provincia, especie)
          </p>
        </div>

        {/* Mostrar línea de tendencia */}
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="showTrendline"
            checked={scatterConfig.showTrendline || false}
            onChange={(e) =>
              onChange({
                ...config,
                scatterPlotConfig: {
                  ...scatterConfig,
                  showTrendline: e.target.checked,
                },
              })
            }
            className="w-5 h-5"
          />
          <label htmlFor="showTrendline" className="font-medium cursor-pointer">
            Mostrar línea de tendencia
          </label>
        </div>

        {/* Configuración del tamaño de puntos */}
        {!scatterConfig.zAxisKey && (
          <div>
            <label className="block font-medium mb-2">
              Tamaño de Puntos (px)
            </label>
            <input
              type="number"
              min="2"
              max="20"
              placeholder="6"
              className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
              value={scatterConfig.pointSize || 6}
              onChange={(e) =>
                onChange({
                  ...config,
                  scatterPlotConfig: {
                    ...scatterConfig,
                    pointSize: parseInt(e.target.value) || 6,
                  },
                })
              }
            />
          </div>
        )}
      </div>

      <div className="flex gap-4 mt-6">
        <button
          onClick={onPrev}
          className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-300"
        >
          ← Atrás
        </button>
        <button
          onClick={onNext}
          disabled={!isValid}
          className="flex-1 bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          Vista Previa →
        </button>
      </div>
    </div>
  )
}